import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import * as api from '../services/api';
import { StatusBadge } from '../components/StatusBadge';
import { EmojiPickerField } from '../components/EmojiPickerField';
import type { Badge } from '../types/badge';

const PAGE_SIZE = 10;

export function BadgesPage() {
  const createModalTitleId = useId();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Badge | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createIcon, setCreateIcon] = useState('🏅');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const visibleBadges = useMemo(() => {
    if (!activeOnly) return badges;
    return badges.filter((badge) => badge.isActive);
  }, [badges, activeOnly]);

  const loadBadges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const badgeItems = await api.listBadges();
      setBadges(badgeItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load badges');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBadges();
  }, [loadBadges]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }

    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      try {
        const badge = await api.getBadge(selectedId!);
        if (!cancelled) {
          setSelected(badge);
          setEditName(badge.name);
          setEditDescription(badge.description ?? '');
          setEditIcon(badge.icon ?? '');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load badge');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const totalPages = Math.max(1, Math.ceil(visibleBadges.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = visibleBadges.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = visibleBadges.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, visibleBadges.length);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    if (!showCreate) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !creating) setShowCreate(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showCreate, creating]);

  function resetCreateForm() {
    setCreateName('');
    setCreateDescription('');
    setCreateIcon('🏅');
    setCreateError(null);
  }

  function openCreateModal() {
    resetCreateForm();
    setShowCreate(true);
  }

  function closeCreateModal() {
    if (creating) return;
    setShowCreate(false);
    resetCreateForm();
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setCreating(true);
    setCreateError(null);
    setSuccess(null);

    const icon = createIcon.trim();
    if (!icon) {
      setCreateError('Icon or emoji is required.');
      setCreating(false);
      return;
    }

    try {
      const badge = await api.createBadge({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        icon
      });
      setBadges((current) => [badge, ...current]);
      setShowCreate(false);
      resetCreateForm();
      setSelectedId(badge.id);
      setSuccess(`Created badge “${badge.name}”. Attach it when creating missions, lectures, or home activities.`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create badge');
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    const icon = editIcon.trim();
    if (!icon) {
      setError('Icon or emoji is required.');
      setSaving(false);
      return;
    }

    try {
      const updated = await api.updateBadge(selected.id, {
        name: editName.trim(),
        description: editDescription.trim() || null,
        icon
      });
      setSelected(updated);
      setBadges((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      setSuccess(`Saved badge “${updated.name}”.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update badge');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(badge: Badge) {
    setBusyId(badge.id);
    setError(null);
    setSuccess(null);
    try {
      const updated = badge.isActive
        ? await api.deactivateBadge(badge.id)
        : await api.reactivateBadge(badge.id);
      setBadges((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      if (selectedId === updated.id) {
        setSelected(updated);
      }
      setSuccess(
        updated.isActive
          ? `Reactivated badge “${updated.name}”.`
          : `Deactivated badge “${updated.name}”.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update badge status');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="admin-panel">
      <div className="panel-heading">
        <div>
          <h2>Badges</h2>
          <p>
            Create badge definitions here. Optionally attach a badge when creating a mission,
            learning lecture, or home activity — students earn it when that item is completed.
          </p>
        </div>
        <span className="panel-meta">
          {visibleBadges.length} shown · {badges.length} total
        </span>
      </div>

      <div className="filter-bar classes-toolbar">
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setPage(1);
              setActiveOnly(e.target.checked);
            }}
          />
          Active only
        </label>
        <button type="button" className="primary-btn" onClick={() => void loadBadges()}>
          Refresh
        </button>
        <button type="button" className="ghost-btn" onClick={openCreateModal}>
          New badge
        </button>
      </div>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      {loading ? <p className="muted">Loading badges…</p> : null}
      {!loading && visibleBadges.length === 0 ? (
        <p className="muted">
          {badges.length === 0
            ? 'No badges yet. Use New badge to create one.'
            : 'No active badges match this filter.'}
        </p>
      ) : null}

      {!loading && visibleBadges.length > 0 ? (
        <div className="schools-split">
          <div className="school-list-panel">
            <div className="table-wrap school-list-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Badge</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((badge) => (
                    <tr
                      key={badge.id}
                      className={`school-row${selectedId === badge.id ? ' is-selected' : ''}`}
                      onClick={() => setSelectedId(badge.id)}
                    >
                      <td>
                        <div className="pillar-name-cell">
                          <span className="pillar-icon-badge" aria-hidden>
                            {badge.icon || '🏅'}
                          </span>
                          <span>
                            <strong>{badge.name}</strong>
                            {badge.description ? (
                              <span className="cell-sub">{badge.description}</span>
                            ) : null}
                          </span>
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={badge.isActive ? 'active' : 'suspended'} />
                      </td>
                      <td>
                        <div className="action-row" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="ghost-btn compact"
                            disabled={busyId === badge.id}
                            onClick={() => void handleToggleActive(badge)}
                          >
                            {badge.isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="school-list-pager" role="navigation" aria-label="Badge list pagination">
              <span className="school-list-pager-range">
                {rangeStart}-{rangeEnd} of {visibleBadges.length}
              </span>
              <button
                type="button"
                className="pager-chevron"
                aria-label="Previous page"
                disabled={safePage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                ‹
              </button>
              <button
                type="button"
                className="pager-chevron"
                aria-label="Next page"
                disabled={safePage >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                ›
              </button>
            </div>
          </div>

          <aside className="school-detail settings-section">
            {!selectedId ? (
              <div className="settings-section-body">
                <p className="muted">Click a badge row to view and edit it.</p>
              </div>
            ) : null}

            {selectedId && detailLoading ? (
              <div className="settings-section-body">
                <p className="muted">Loading badge…</p>
              </div>
            ) : null}

            {selected && !detailLoading ? (
              <>
                <div className="settings-section-header">
                  <h3>
                    <span className="pillar-icon-badge pillar-icon-badge-lg" aria-hidden>
                      {selected.icon || '🏅'}
                    </span>
                    {selected.name}
                  </h3>
                  <p className="school-detail-meta">
                    <StatusBadge status={selected.isActive ? 'active' : 'suspended'} />
                  </p>
                </div>
                <div className="settings-section-body">
                  <form className="stacked-form" onSubmit={handleSave}>
                    <label>
                      Name
                      <input
                        required
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    </label>
                    <label>
                      Icon / emoji
                      <EmojiPickerField
                        required
                        value={editIcon}
                        onChange={setEditIcon}
                        placeholder="Select emoji"
                      />
                    </label>
                    <label>
                      Description
                      <textarea
                        rows={3}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Optional"
                      />
                    </label>
                    <p className="muted">
                      Attach this badge on Character Pillars when creating or editing a mission,
                      lecture, or home activity.
                    </p>
                    <div className="action-row">
                      <button type="submit" className="primary-btn" disabled={saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                      <button
                        type="button"
                        className="ghost-btn"
                        disabled={busyId === selected.id}
                        onClick={() => void handleToggleActive(selected)}
                      >
                        {selected.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            ) : null}
          </aside>
        </div>
      ) : null}

      {showCreate ? (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeCreateModal();
          }}
        >
          <div
            className="admin-modal pillar-create-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={createModalTitleId}
          >
            <div className="admin-modal-header">
              <div>
                <h3 id={createModalTitleId}>Create badge</h3>
                <p className="modal-subtitle">
                  Define the badge here. Assign it later on a mission, lecture, or home activity.
                </p>
              </div>
              <button
                type="button"
                className="ghost-btn compact"
                disabled={creating}
                onClick={closeCreateModal}
              >
                Close
              </button>
            </div>

            <form className="admin-modal-body" onSubmit={handleCreate}>
              {createError ? (
                <p className="form-error" role="alert">
                  {createError}
                </p>
              ) : null}

              <div className="pillar-basics-grid">
                <label>
                  Name
                  <input
                    required
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="e.g. Mission Champion"
                  />
                </label>
                <label>
                  Icon / emoji
                  <EmojiPickerField
                    required
                    value={createIcon}
                    onChange={setCreateIcon}
                    placeholder="Select emoji"
                  />
                </label>
                <label className="form-span-2">
                  Description
                  <textarea
                    rows={3}
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    placeholder="Optional — shown with the badge"
                  />
                </label>
              </div>

              <div className="admin-modal-actions">
                <button type="submit" className="primary-btn" disabled={creating}>
                  {creating ? 'Creating…' : 'Create badge'}
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  disabled={creating}
                  onClick={closeCreateModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
